import { useLocation, useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { Box, Tabs, Tab, Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomerTopbar from "../../features/manageCustomers/topbar";
import CustomerListPending from "../../features/manageCustomers/customeListPending";
import CustomerListApproved from "../../features/manageCustomers/customeListApproved";
import CustomerListRejected from "../../features/manageCustomers/customeListRejected";
import CustomerListExpired from "../../features/manageCustomers/customeListExpired";
import {
  useLazyExportUserQuery,
  useLazyGetUsersQuery,
} from "../../service/user";
import { showError } from "../../constants/toasts";
import Loader from "../../constants/Loader";
import CustomerListCancelled from "../../features/manageCustomers/customerListCancelled";
import dayjs, { Dayjs } from "dayjs";
import MemberFilter from "../../components/MemberFilter";
import {
  useLazyGetDietaryQuery,
  useLazyGetExpertiseQuery,
  useLazyGetInterestQuery,
} from "../../service/cms";
import { useAppDispatch, useAppSelector } from "../../hooks/store";
import { RootState } from "../../app/store";
import {
  resetMemberFilters,
  setMemberFilters,
} from "../../reducers/memberFiltersSlice";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

enum UserStatus {
  Pending = "Pending",
  Approved = "Approved",
  Rejected = "Rejected",
  Inactive = "Expired",
  Cancelled = "cancelsubscription",
  All = "",
}

interface Interests {
  created: string;
  id: number;
  isActive: boolean;
  name: string;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageCustomers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [getInterest] = useLazyGetInterestQuery();
  const [getExpertise] = useLazyGetExpertiseQuery();
  const [getDietary] = useLazyGetDietaryQuery();
  const [exportUser] = useLazyExportUserQuery();
  const searchParams = new URLSearchParams(location.search);
  const tab = searchParams.get("tab");

  const dispatch = useAppDispatch();
  const memberFilters = useAppSelector(
    (state: RootState) => state.memberFilters
  );

  const [getUsers] = useLazyGetUsersQuery();

  const [loading, setLoading] = useState(false);

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    navigate(`?tab=${newValue}`);
    setPage(1);
    setValueTabs(newValue);
  };

  const handleFilterChange = (key: string, value: any) => {
    dispatch(setMemberFilters({ [key]: value }));
  };

  const handleClearAll = () => {
    dispatch(resetMemberFilters());
  };

  const tabIndexFromState = location.state?.tabIndex ?? 0;

  const [valueTabs, setValueTabs] = React.useState(tabIndexFromState);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [users, setUsers] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [interests, setInterests] = useState<Interests[]>([]);
  const [expertise, setExpertise] = useState([]);
  const [dietary, setDietary] = useState([]);
  const [currentOrder, setCurrentOrder] = useState("DateCreated_desc");

  const statusMap: Record<number, UserStatus> = {
    0: UserStatus.Pending,
    1: UserStatus.Approved,
    2: UserStatus.Rejected,
    3: UserStatus.Inactive,
    4: UserStatus.Cancelled,
  };

  const getUsersApi = async (clearData?: boolean) => {
    setLoading(true);
    try {
      const body: Record<string, any> = {
        search: debouncedSearchTerm.trim(),
        pageNumber: page,
        pageSize: 10,
        ...(memberFilters.activeMembersDays && !clearData
          ? { activeMemberdays: 30 }
          : {}),
        ...(memberFilters.gender
          ? { gender: clearData ? null : memberFilters.gender }
          : {}),
        ...(memberFilters.membershipType
          ? { membershipType: clearData ? null : memberFilters.membershipType }
          : {}),
        ...(memberFilters.membership && !clearData
          ? {
              membership: memberFilters.membership,
            }
          : {}),
        ...(memberFilters.isNewMember
          ? { isNewMember: memberFilters.isNewMember }
          : {}),
        ...(memberFilters.startDate
          ? {
              startDate: clearData
                ? null
                : dayjs(memberFilters.startDate).format("YYYY-MM-DDTHH:mm:ss"),
            }
          : {}),
        ...(memberFilters.endDate
          ? {
              endDate: clearData
                ? null
                : dayjs(memberFilters.endDate).format("YYYY-MM-DDTHH:mm:ss"),
            }
          : {}),
        ...(memberFilters.minAge
          ? { minAge: clearData ? null : memberFilters.minAge }
          : {}),
        ...(memberFilters.maxAge
          ? { maxAge: clearData ? null : memberFilters.maxAge }
          : {}),
        ...(memberFilters.expertise
          ? { expertise: clearData ? null : memberFilters.expertise }
          : {}),
        ...(memberFilters.dietary
          ? { dietary: clearData ? null : memberFilters.dietary }
          : {}),
        ...(memberFilters.interests
          ? { interests: clearData ? null : memberFilters.interests }
          : {}),
        ...(memberFilters.subscriptionType
          ? {
              subscriptionType: clearData
                ? null
                : memberFilters.subscriptionType,
            }
          : {}),
        ...(memberFilters.subscriptionStartDate
          ? {
              subscriptionStartDate: clearData
                ? null
                : dayjs(memberFilters.subscriptionStartDate).format(
                    "YYYY-MM-DDTHH:mm:ss"
                  ),
            }
          : {}),
        ...(memberFilters.expiryDate
          ? {
              expiryDate: clearData
                ? null
                : dayjs(memberFilters.expiryDate).format("YYYY-MM-DDTHH:mm:ss"),
            }
          : {}),
        ...(memberFilters.onlyLoggedIn
          ? { onlyLoggedIn: clearData ? false : memberFilters.onlyLoggedIn }
          : {}),
        ...(memberFilters.notCreatedOrder
          ? {
              notCreatedOrder: clearData
                ? false
                : memberFilters.notCreatedOrder,
            }
          : {}),
        ...(memberFilters.expiringInDays && !clearData
          ? {
              expiringInDays: memberFilters.expiringInDays,
            }
          : {}),
        ...(memberFilters.newSignupMembers && !clearData
          ? {
              newSignupMembers: memberFilters.newSignupMembers,
            }
          : {}),
        ...(memberFilters.inactiveMembers && !clearData
          ? { inactiveMembers: memberFilters.inactiveMembers }
          : {}),
      };

      const response = await getUsers({
        page: page,
        search: debouncedSearchTerm.trim(),
        status: tab
          ? statusMap[Number(tab)]
          : statusMap[valueTabs] || UserStatus.All,
        body: body,
        sortOrder: currentOrder,
      }).unwrap();

      setHasFetched(true);
      setLoading(false);

      if (response.status === 200) {
        setUsers(response?.data?.paginatedResult?.items || []);
        setCount(response?.data?.paginatedResult?.totalCount);
        setCurrentOrder(response?.data?.sortOrder || "");
      } else {
        setUsers([]);
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const exportUserApi = async (clearData?: boolean) => {
    setLoading(true);
    try {
      const body: Record<string, any> = {
        ...(memberFilters.activeMembersDays && !clearData
          ? { activeMemberdays: 30 }
          : {}),
        ...(memberFilters.gender
          ? { gender: clearData ? null : memberFilters.gender }
          : {}),
        ...(memberFilters.membershipType
          ? { membershipType: clearData ? null : memberFilters.membershipType }
          : {}),
        ...(memberFilters.membership && !clearData
          ? {
              membership: memberFilters.membership,
            }
          : {}),
        ...(memberFilters.isNewMember
          ? { isNewMember: memberFilters.isNewMember }
          : {}),
        ...(memberFilters.startDate
          ? {
              startDate: clearData
                ? null
                : memberFilters.startDate?.toISOString(),
            }
          : {}),
        ...(memberFilters.endDate
          ? { endDate: clearData ? null : memberFilters.endDate?.toISOString() }
          : {}),
        ...(memberFilters.minAge
          ? { minAge: clearData ? null : memberFilters.minAge }
          : {}),
        ...(memberFilters.maxAge
          ? { maxAge: clearData ? null : memberFilters.maxAge }
          : {}),
        ...(memberFilters.expertise
          ? { expertise: clearData ? null : memberFilters.expertise }
          : {}),
        ...(memberFilters.dietary
          ? { dietary: clearData ? null : memberFilters.dietary }
          : {}),
        ...(memberFilters.interests
          ? { interests: clearData ? null : memberFilters.interests }
          : {}),
        ...(memberFilters.subscriptionType
          ? {
              subscriptionType: clearData
                ? null
                : memberFilters.subscriptionType,
            }
          : {}),
        ...(memberFilters.subscriptionStartDate
          ? {
              subscriptionStartDate: clearData
                ? null
                : memberFilters.subscriptionStartDate?.toISOString(),
            }
          : {}),
        ...(memberFilters.expiryDate
          ? {
              expiryDate: clearData
                ? null
                : memberFilters.expiryDate?.toISOString(),
            }
          : {}),
        ...(memberFilters.onlyLoggedIn
          ? { onlyLoggedIn: clearData ? false : memberFilters.onlyLoggedIn }
          : {}),
        ...(memberFilters.notCreatedOrder
          ? {
              notCreatedOrder: clearData
                ? false
                : memberFilters.notCreatedOrder,
            }
          : {}),
        ...(memberFilters.expiringInDays && !clearData
          ? {
              expiringInDays: memberFilters.expiringInDays,
            }
          : {}),
        ...(memberFilters.newSignupMembers && !clearData
          ? {
              newSignupMembers: memberFilters.newSignupMembers,
            }
          : {}),
        ...(memberFilters.inactiveMembers && !clearData
          ? { inactiveMembers: memberFilters.inactiveMembers }
          : {}),
      };

      const response: any = await exportUser({
        format: "excel",
        search: debouncedSearchTerm.trim(),
        status: tab
          ? statusMap[Number(tab)]
          : statusMap[valueTabs] || UserStatus.All,
        body: body,
      }).unwrap();

      setHasFetched(true);
      setLoading(false);

      if (response.statusCode === 200) {
        window.open(response?.downloadUrl, "_blank");
      }
    } catch (error: any) {
      setHasFetched(true);
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getInterestApi: any = async () => {
    setLoading(true);
    try {
      const response = await getInterest({
        page: 1,
        search: "",
      }).unwrap();
      if (response?.status === 200) {
        setLoading(false);
        setInterests(response?.data || []);
        // setCount(response?.data?.totalCount || 0);
      } else {
        setLoading(false);
        setInterests([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getExpertiseApi: any = async () => {
    setLoading(true);
    try {
      const response = await getExpertise({
        page: 1,
        search: "",
      }).unwrap();
      if (response?.status === 200) {
        setLoading(false);
        setExpertise(response?.data?.items || []);
      } else {
        setLoading(false);
        setInterests([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getDietaryApi: any = async () => {
    setLoading(true);
    try {
      const response = await getDietary({
        page: 1,
        search: "",
      }).unwrap();
      if (response?.status === 200) {
        setLoading(false);
        setDietary(response?.data || []);
      } else {
        setLoading(false);
        setInterests([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    if (currentOrder) {
      getUsersApi();
    }
  }, [valueTabs, debouncedSearchTerm, page, tab, currentOrder]);

  useEffect(() => {
    if (tab) {
      setValueTabs(Number(tab));
    }
  }, [tab]);

  useEffect(() => {
    getInterestApi();
    getExpertiseApi();
    getDietaryApi();
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title d_flex mb_30">
        <h1>User Management</h1>

        <div className="right_s">
          <Button
            className="btn btn_primary"
            onClick={() =>
              navigate("/manage-customers/add", {
                state: {
                  type: "ADD",
                },
              })
            }
          >
            + Add User
          </Button>
          <Button className="btn btn_primary" onClick={() => exportUserApi()}>
            <img src="/static/images/export_icon.svg" alt="icon" /> Export Data
          </Button>

          <MemberFilter
            handleSubmit={getUsersApi}
            startDate={memberFilters.startDate}
            endDate={memberFilters.endDate}
            setStartDate={(date: Dayjs | null) =>
              handleFilterChange("startDate", date)
            }
            setEndDate={(date: Dayjs | null) =>
              handleFilterChange("endDate", date)
            }
            membershipType={memberFilters.membershipType}
            setMembershipType={(value: string) =>
              handleFilterChange("membershipType", value)
            }
            membership={memberFilters.membership}
            setMembership={(value: string) =>
              handleFilterChange("membership", value)
            }
            subscriptionType={memberFilters.subscriptionType}
            setSubscriptionType={(value: string) =>
              handleFilterChange("subscriptionType", value)
            }
            gender={memberFilters.gender}
            setGender={(value: string) => handleFilterChange("gender", value)}
            subscriptionStartDate={memberFilters.subscriptionStartDate}
            setSubscriptionStartDate={(date: Dayjs | null) =>
              handleFilterChange("subscriptionStartDate", date)
            }
            expiryDate={memberFilters.expiryDate}
            setExpiryDate={(date: Dayjs | null) =>
              handleFilterChange("expiryDate", date)
            }
            interests={interests}
            expertise={expertise}
            dietary={dietary}
            selectedExpertise={memberFilters.expertise}
            setSelectedExpertise={(value: string[]) =>
              handleFilterChange("expertise", value)
            }
            selectedDietary={memberFilters.dietary}
            setSelectedDietary={(value: string[]) =>
              handleFilterChange("dietary", value)
            }
            selectedInterests={memberFilters.interests}
            setSelectedInterests={(value: string[]) =>
              handleFilterChange("interests", value)
            }
            onlyLoggedIn={memberFilters.onlyLoggedIn}
            setOnlyLoggedIn={(value: boolean) =>
              handleFilterChange("onlyLoggedIn", value)
            }
            notCreatedOrder={memberFilters.notCreatedOrder}
            setNotCreatedOrder={(value: boolean) =>
              handleFilterChange("notCreatedOrder", value)
            }
            activeMembersDays={memberFilters.activeMembersDays}
            setActiveMembersDays={(value: boolean) =>
              handleFilterChange("activeMembersDays", value)
            }
            inactiveMembers={memberFilters.inactiveMembers}
            setInactiveMembers={(value: boolean) =>
              handleFilterChange("inactiveMembers", value)
            }
            newSignupMembers={memberFilters.newSignupMembers}
            setNewSignupMembers={(value: boolean) =>
              handleFilterChange("newSignupMembers", value)
            }
            expiringInDays={memberFilters.expiringInDays}
            setExpiringInDays={(value: boolean) =>
              handleFilterChange("expiringInDays", value)
            }
            minAge={memberFilters.minAge}
            setMinAge={(value: number) => handleFilterChange("minAge", value)}
            maxAge={memberFilters.maxAge}
            setMaxAge={(value: number) => handleFilterChange("maxAge", value)}
            handleClearFilters={handleClearAll}
          />
        </div>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Pending" {...a11yProps(0)} />
              <Tab label="Approved" {...a11yProps(1)} />
              <Tab label="Rejected" {...a11yProps(2)} />
              <Tab label="Expired" {...a11yProps(3)} />
              <Tab label="Cancelled" {...a11yProps(4)} />
            </Tabs>
          </Box>
        </div>
        <div className="right_s">
          <CustomerTopbar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            debouncedSearchTerm={debouncedSearchTerm}
            setDebouncedSearchTerm={setDebouncedSearchTerm}
          />
        </div>
      </div>

      <div className="table_wraper">
        <CustomTabPanel value={valueTabs} index={0}>
          <CustomerListPending
            data={users}
            debouncedSearchTerm={debouncedSearchTerm}
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            getUsersApi={getUsersApi}
            hasFetched={hasFetched}
            count={count}
            page={page}
            setPage={setPage}
            sortOrder={currentOrder}
            setSortOrder={setCurrentOrder}
          />
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          <CustomerListApproved
            data={users}
            hasFetched={hasFetched}
            count={count}
            page={page}
            setPage={setPage}
            sortOrder={currentOrder}
            setSortOrder={setCurrentOrder}
          />
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={2}>
          <CustomerListRejected
            data={users}
            hasFetched={hasFetched}
            count={count}
            page={page}
            setPage={setPage}
            sortOrder={currentOrder}
            setSortOrder={setCurrentOrder}
          />
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={3}>
          <CustomerListExpired
            data={users}
            hasFetched={hasFetched}
            count={count}
            page={page}
            setPage={setPage}
            sortOrder={currentOrder}
            setSortOrder={setCurrentOrder}
          />
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={4}>
          <CustomerListCancelled
            data={users}
            hasFetched={hasFetched}
            count={count}
            page={page}
            setPage={setPage}
            sortOrder={currentOrder}
            setSortOrder={setCurrentOrder}
          />
        </CustomTabPanel>
      </div>
    </MainContainer>
  );
};

export default ManageCustomers;
